/**
 * Provider toggle segments for filtering chart data
 *
 * Two modes:
 * - Single select (default): Click a provider to show only that one
 * - Compare mode: Toggle to add/remove providers for comparison
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { X, Check } from 'lucide-react'
import type { Provider } from './types'

interface ProviderSegmentsProps {
  providers: Provider[]
  selectedProviders: string[]
  compareMode: boolean
  onSelect: (providerId: string) => void
  onToggleCompare: (providerId: string) => void
  onSelectAll: () => void
  onCompareModeChange: (enabled: boolean) => void
}

export const ProviderSegments: React.FC<ProviderSegmentsProps> = ({
  providers,
  selectedProviders,
  compareMode,
  onSelect,
  onToggleCompare,
  onSelectAll,
  onCompareModeChange,
}) => {
  const { t } = useTranslation('aiCostTrends')
  const allSelected = selectedProviders.length === 0

  const handleProviderClick = (providerId: string) => {
    if (compareMode) {
      onToggleCompare(providerId)
    } else {
      onSelect(providerId)
    }
  }

  // Compare mode: different visual treatment
  if (compareMode) {
    return (
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-accent/10 rounded-lg border border-accent/30">
        <span className="text-xs text-accent font-medium mr-1">
          {t('chart.compareModeLabel')}
        </span>

        {/* Provider checkboxes */}
        {providers.map((provider) => {
          const isSelected = selectedProviders.includes(provider.id)

          return (
            <button
              key={provider.id}
              onClick={() => onToggleCompare(provider.id)}
              className={`px-2.5 py-1 text-sm rounded transition-colors flex items-center gap-1.5 ${
                isSelected
                  ? 'bg-accent/20 text-primary'
                  : 'bg-tertiary/50 text-secondary hover:text-primary'
              }`}
            >
              {/* Checkbox indicator */}
              <span
                className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${
                  isSelected
                    ? 'bg-accent border-accent'
                    : 'border-secondary'
                }`}
              >
                {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
              </span>
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: provider.color }}
              />
              {provider.name}
            </button>
          )
        })}

        {/* Exit compare mode */}
        <button
          onClick={() => onCompareModeChange(false)}
          className="ml-auto px-2 py-1 text-xs text-accent hover:text-primary flex items-center gap-1 rounded hover:bg-accent/10 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
          {t('chart.exitCompare')}
        </button>
      </div>
    )
  }

  // Normal mode: single select
  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* All button */}
      <button
        onClick={onSelectAll}
        className={`px-3 py-1.5 text-sm rounded-md transition-colors border ${
          allSelected
            ? 'bg-tertiary border-accent text-primary'
            : 'bg-tertiary border-transparent text-secondary hover:text-primary hover:border-secondary'
        }`}
      >
        {t('chart.all')}
      </button>

      {/* Provider buttons - single select */}
      {providers.map((provider) => {
        const isSelected = selectedProviders.length === 1 && selectedProviders.includes(provider.id)

        return (
          <button
            key={provider.id}
            onClick={() => handleProviderClick(provider.id)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2 border ${
              isSelected
                ? 'bg-tertiary border-accent text-primary'
                : 'bg-tertiary border-transparent text-secondary hover:text-primary hover:border-secondary'
            }`}
          >
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: provider.color }}
            />
            {provider.name}
          </button>
        )
      })}

      {/* Compare mode toggle - styled as a checkbox */}
      <div className="ml-2 pl-2 border-l border-secondary flex items-center gap-2">
        <label className="flex items-center gap-1.5 cursor-pointer text-sm text-secondary hover:text-primary">
          <input
            type="checkbox"
            checked={compareMode}
            onChange={(e) => onCompareModeChange(e.target.checked)}
            className="w-3.5 h-3.5 rounded border-secondary accent-accent cursor-pointer"
          />
          {t('chart.compare')}
        </label>
      </div>
    </div>
  )
}

export default ProviderSegments
